<template>
  <div>
    <ValidationObserver ref="form">
      <v-card flat>
        <v-card-title v-show="showTitle">
          {{ $t("app.menu.MY_PASSWORD") }}
        </v-card-title>
        <v-card-text class="pa-2 pb-0">
          <rx-text-field
            id="currentPassword"
            name="currentPassword"
            type="password"
            v-model="form.fields.currentPassword"
            :label="$t('password.lbl.current_password')"
            :rules="rules.currentPassword"
            prepend-inner-icon="$vuetify.icons.fld_pword"
            outlined
            :dense="false"
          ></rx-text-field>
          <rx-text-field
            type="password"
            id="newPassword"
            name="newPassword"
            v-model="form.fields.newPassword"
            :label="$t('password.lbl.new_password')"
            :rules="rules.newPassword"
            prepend-inner-icon="$vuetify.icons.fld_new_pword"
            :hint="
              authPolicy.passwordPattern.usable
                ? $t('password.msg.passsword_pattern_hint', {
                    patternHint: patternHint,
                  })
                : ''
            "
            :loading="form.fields.newPassword != null"
            outlined
            :dense="false"
          >
            <template v-slot:progress>
              <v-progress-linear
                :value="checkStrength.progress"
                :color="checkStrength.color"
                height="7"
                absolute
              ></v-progress-linear>
              <v-chip
                small
                outlined
                :color="checkStrength.color"
                style="align-self: center"
              >
                <v-icon small>$fld_security</v-icon>
                {{ `${$t("password.lbl.strength")} : ${checkStrength.label}` }}
              </v-chip>
            </template>
          </rx-text-field>
          <rx-text-field
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            v-model="form.fields.confirmPassword"
            :label="$t('password.lbl.confirm_password')"
            :rules="rules.confirmPassword"
            prepend-inner-icon="$vuetify.icons.fld_confirm_pword"
            outlined
            :dense="false"
          ></rx-text-field>
        </v-card-text>
        <v-divider v-if="useActions"></v-divider>
        <v-card-actions v-if="useActions">
          <v-spacer></v-spacer>
          <slot name="prepend-action">
            <rx-btn
              @click="changePasswordAndLogout"
              color="rx-btn save"
              :loading="loading"
              depressed
              dark
            >
              <v-icon left>$vuetify.icons.fld_check</v-icon>
              {{ $t("password.btn.change_password") }}
            </rx-btn>
          </slot>
        </v-card-actions>
      </v-card>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver, extend } from "vee-validate";
import { $getAuthPolicy } from "@/redworks/api/settings";
import { $updateMyPassword } from "@/redworks/api/my";
import i18n from "@/redworks/plugins/i18n";
import { AUTH_RST_CD, $logout } from "@/redworks/api/auth";
import { mapGetters } from "vuex";
export default {
  components: {
    ValidationObserver,
  },
  props: {
    showTitle: {
      type: Boolean,
      default: true,
    },
    showChangeNext: {
      type: Boolean,
      default: false,
    },
    useActions: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapGetters("auth", {
      authorization: "getAuthorization",
      refreshTokenWithoutPrefix: "getRefreshTokenWithoutPrefix", // logout에 필요
      // loginUserId: "getUserId",
    }),
    loginId() {
      return this.authorization.loginId;
    },
    rules() {
      return {
        currentPassword: {
          required: () => true,
        },
        newPassword: {
          required: () => true,
          duplicated: {
            currentPassword: this.form.fields.currentPassword,
          },
          passwordPattern: {
            patternRegex: this.makePattern(),
            patternHint: this.makePatternHint(),
            checkLimitString: this.checkLimitString(),
          },
          max:200
        },
        confirmPassword: {
          required: () => true,
          confirmed: {
            newPassword: this.form.fields.newPassword,
          },
        },
      };
    },
    patternHint() {
      return this.makePatternHint();
    },
    checkStrength() {
      // const spaceRegex = new RegExp("\\s");
      const digitRegex = new RegExp("(?=.*\\d)");
      const lowcaseRegex = new RegExp("(?=.*[a-z])");
      const uppercaseRegex = new RegExp("(?=.*[A-Z])");
      const symbolicRegex = new RegExp(
        `(?=.*[/?<>{}[\\]{}();:\`'\\\\"|=+!@#$%,~^&.*\\_-])`
      );
      let strength = 0;
      const password = this.form.fields.newPassword;
      // if(!spaceRegex.test(password) && lowcaseRegex.test(password)){
      //   strength += 20;
      // }
      if (digitRegex.test(this.form.fields.newPassword)) {
        strength += 20;
      }
      if (lowcaseRegex.test(password)) {
        strength += 20;
      }
      if (uppercaseRegex.test(password)) {
        strength += 20;
      }
      if (symbolicRegex.test(password)) {
        strength += 20;
      }
      if (null != this.form.fields.newPassword && password.length > 12) {
        strength += 20;
      }
      let color = "";
      let label = "";
      switch (strength) {
        case 100:
          color = "green";
          label = this.$t("password.lbl.strong");
          break;
        case 80:
          color = "blue";
          label = this.$t("password.lbl.fair");
          break;
        default:
          color = "orange";
          label = this.$t("password.lbl.weak");
          break;
      }
      return {
        progress: strength,
        color: color,
        label: label,
      };
    },
    continualRegex() {
      const keyboardRows = [
        "qwertyuiop",
        "asdfghjkl",
        "zxcvbnm",
        "1234567890",
      ]

      return keyboardRows.map(row => {
        const chars = row.split('');
        const forwardPattern = chars.reduce((acc, char, idx) => {
          if (idx <= chars.length - 3) {
            acc.push(`${char}${chars[idx + 1]}${chars[idx + 2]}`);
          }
          return acc;
        }, []);

        // 역방향으로도 3개 연속 문자를 추출
        const reversePattern = chars.reverse().reduce((acc, char, idx) => {
          if (idx <= chars.length - 3) {
            acc.push(`${char}${chars[idx + 1]}${chars[idx + 2]}`);
          }
          return acc;
        }, []);

        // 순방향과 역방향 패턴을 합침
        return [...forwardPattern, ...reversePattern].join('|');
      }).join("|");
    }
  },
  data() {
    return {
      form: {
        fields: {
          currentPassword: null,
          newPassword: null,
          confirmPassword: null,
        },
      },
      authPolicy: {
        passwordPattern: {
          usable: true,
          includeUpperCase: true,
          includeDigit: true,
          includeSymbolic: true,
          validateSameString: true,
          validateContinuousString: true,
          validateMinLength: true,
          minLength: 6,
        },
      },
      loading: false,
    };
  },
  methods: {
    async changePassword() {
      if (await this.$refs.form.validate()) {
        this.loading = true;
        try {
          const params = {
            originPassword: this.CryptoJS.SHA256(
              this.form.fields.currentPassword
            ).toString(this.CryptoJS.enc.Hex),
            newPassword: this.CryptoJS.SHA256(
              this.form.fields.newPassword
            ).toString(this.CryptoJS.enc.Hex),
          };

          // 비밀번호 변경
          await $updateMyPassword(params);

          // 성공 후, Form init()
          this.initForm();

          return true;
        } catch (error) {
          if (error.response.status === 417) {
            if (error.response.data.messageCode === "417-1") {
              this.$rxSnackbar("warning", this.$t("login.msg.login_failed"));
            } else if (error.response.data.messageCode === "417-2") {
              this.$rxSnackbar(
                "warning",
                this.$t("password.msg.can_not_use_same_password")
              );
            }
          } else {
            this.$rxSnackbar("error", this.$t("common.msg.unexpected_error"));
          }
          return false;
        } finally {
          this.loading = false;
        }
      }
    },
    async changePasswordAndLogout() {
      if (await this.changePassword()) {
        // 성공 후, Logout 으로 이동 확인
        if (
          await this.$rxAlert(
            "success",
            this.$t("common.lbl.changed_successfully", [
              this.$t("password.lbl.password"),
            ]),
            `${this.$t("common.msg.change_successful", [
              this.$t("password.lbl.password"),
            ])} \n${this.$t("common.msg.please_re_login")}`
          )
        ) {
          await this.logout();
        }
      }
    },
    async logout() {
      const { data } = await $logout(this.refreshTokenWithoutPrefix);
      if (data.status === AUTH_RST_CD.SUCCESS) {
        this.$store.dispatch("auth/initAuthorization");
        this.$rxSnackbar("success", this.$t("common.msg.logout_successful"));
        this.$router.push(this.RX_PAGE.LOGIN).catch(() => {});
      }
    },
    makePattern() {
      const digitRegex = new RegExp("(?=.*\\d)");
      const lowcaseRegex = `(?=.*[a-z])`;
      const uppercaseRegex = new RegExp("(?=.*[A-Z])");
      const symbolicRegex = new RegExp(
        `(?=.*[/?<>{}[\\]{}();:\`'\\\\"|=+!@#$%,~^&.*\\_-])`
      );
      const samePatters = new RegExp(`^(?!.*${this.loginId}).*`);
      const continualPattern1 = new RegExp("^(?!.*(.)\\1\\1).*");
      const continualPattern2 = new RegExp(`^(?=(?!.*(${this.continualRegex})).*)`);
      const passwordPattern = this.authPolicy.passwordPattern;
      // let regexStr = new RegExp('\s','g');
      let regexStr = new RegExp(lowcaseRegex);
      // let patterns = [];
      // 패스워드 패턴 사용 시
      if (passwordPattern.usable) {
        if (passwordPattern.includeUpperCase) {
          // 대문자 포함 형식 추가
          regexStr = new RegExp(regexStr.source + uppercaseRegex.source);
        }
        if (passwordPattern.includeDigit) {
          // 숫자 포함
          regexStr = new RegExp(regexStr.source + digitRegex.source);
        }
        if (passwordPattern.includeSymbolic) {
          // 특수 문자
          regexStr = new RegExp(regexStr.source + symbolicRegex.source);
        }
        if (passwordPattern.validateSameString) {
          // 동일한 문자열 제한
          regexStr = new RegExp(regexStr.source + samePatters.source);
        }
        if (passwordPattern.validateContinuousString) {
          // 연속된 문자열 3개 이상 제한
          regexStr = new RegExp(regexStr.source + continualPattern1.source);
          regexStr = new RegExp(regexStr.source + continualPattern2.source);
        }
        if (passwordPattern.validateMinLength) {
          // 자리수 체크
          regexStr = new RegExp(
            regexStr.source + `.{${passwordPattern.minLength},}`
          );
        } else {
          regexStr = new RegExp(regexStr.source + `.{1,}`);
        }
      }
      return regexStr;
    },
    makePatternHint() {
      const passwordPattern = this.authPolicy.passwordPattern;
      // 패스워드 패턴 사용 시
      let patterns = [this.$t("common.lbl.lower_case")];
      if (passwordPattern.usable) {
        if (passwordPattern.includeUpperCase) {
          // 대문자 포함 형식 추가
          patterns.push(this.$t("common.lbl.upper_case"));
        }
        if (passwordPattern.includeDigit) {
          // 숫자 포함
          patterns.push(this.$t("common.lbl.digit"));
        }
        if (passwordPattern.includeSymbolic) {
          // 특수 문자
          patterns.push(this.$t("common.lbl.symbols"));
        }
        if (passwordPattern.validateMinLength) {
          // 자리수 체크
          patterns.push(
            this.$t("common.lbl.at_least", [passwordPattern.minLength])
          );
        }
        if (passwordPattern.validateSameString) {
          patterns.push(this.$t("common.lbl.exclude_same_string"));
        }
        if (passwordPattern.validateContinuousString) {
          patterns.push(this.$t("common.lbl.exclude_continuous_string"));
        }
      }
      let hint = "";
      if (patterns.length > 0) {
        patterns.forEach((pattern, i) => {
          if (i > 0) {
            hint = hint.concat("/");
          }
          hint = hint.concat(pattern);
        });
      }
      return hint;
    },
    checkLimitString() {
      let isLimitCheck = true;
      const passwordPattern = this.authPolicy.passwordPattern;
      if (passwordPattern.usable) {
        if(this.form.fields.newPassword != null 
              &&passwordPattern.validateLimitString && passwordPattern.limitString != "") {
          // 사용불가 문자열 제한 체크
          passwordPattern.limitString.split("\n").forEach(str => {
            if(this.form.fields.newPassword.includes(str)){
              isLimitCheck = false;
            }
          });
        }
      }

      return isLimitCheck;
    },
    async getAuthPolicy() {
      //인증 정책 취득
      try {
        const { data } = await $getAuthPolicy();
        this.authPolicy = JSON.parse(data.result.configVal);
      } catch {
        this.$rxSnackbar("error", this.$t("common.msg.unexpected_error"));
      }
    },
    initForm() {
      this.form.fields.currentPassword = null;
      this.form.fields.newPassword = null;
      this.form.fields.confirmPassword = null;
      this.$refs.form.reset();
    },
  },
  async created() {
    // 어플리케이션 비밀번호 정책 정보 취득
    if (this.authorization) {
      await this.getAuthPolicy();
    }
    /** vee-validate rule extend */
    extend("duplicated", {
      validate(value, { currentPassword }) {
        return value !== currentPassword;
      },
      params: ["currentPassword"],
      message: () => {
        return i18n.t("password.msg.can_not_use_same_password");
      },
    });
    extend("passwordPattern", {
      validate(value, args) {
        // 1. 공백 체크
        const spaceRegex = new RegExp("\\s");
        if (spaceRegex.test(value)) {
          return false;
        } else {
          // 3. 패턴 값 포함 여부 && 제한 단어 체크
          return args.patternRegex.test(value) && args.checkLimitString;
        }
        // TODO: 2. 허용 패턴 이외의 값 체크
      },
      params: ["patternRegex", "patternHint", "checkLimitString"],
      message: (_, value) => {
        if(!value.checkLimitString) {
          return i18n.t("password.msg.can_not_use_limit_string_password");
        } else {
          return i18n.t("password.msg.passsword_pattern_hint", {
            patternHint: value.patternHint,
          });
        }
      },
    });
    extend("confirmed", {
      validate(value, { newPassword }) {
        return value === newPassword;
      },
      params: ["newPassword"],
      message: (_, value) => {
        return i18n.t("password.msg.password_do_not_match", {
          newPassword: value.newPassword,
        });
      },
    });
  },
};
</script>

<style></style>
